import React, { useRef, useState, useEffect } from 'react'

const LazyImage = React.memo(
  ({
    src,
    alt = '',
    className = '',
    style = {},
    placeholder = null,
    backUpImage = '',
    imgClassName = '',
    imgStyle = {},
    eager = false,
    blurPlaceholder = null, // Base64 tiny blur placeholder for progressive loading
    ...props
  }) => {
    const [isVisible, setIsVisible] = useState(eager ? true : false)
    const [isLoaded, setIsLoaded] = useState(false)
    const [hasError, setHasError] = useState(false)
    const imgRef = useRef()

    useEffect(() => {
      if (eager) {
        setIsVisible(true)
        return
      }
      let observer
      if (imgRef.current && !isVisible) {
        observer = new window.IntersectionObserver(
          ([entry]) => {
            if (entry.isIntersecting) {
              setIsVisible(true)
              observer.disconnect()
            }
          },
          { rootMargin: '100px' }
        )
        observer.observe(imgRef.current)
      }
      return () => observer && observer.disconnect()
    }, [isVisible, eager])

    // Normalize src: support string URLs, ESM module objects (default), and SVG React components
    const renderImage = () => {
      // If src is a React element, render it directly
      if (React.isValidElement(src)) return src

      // If src is a React component (function/class), render it as component
      if (typeof src === 'function') {
        const SvgComp = src
        return <SvgComp className={imgClassName || className} style={{ ...imgStyle }} {...props} />
      }

      // If src is an ESM module (e.g. { default: '/url' }), prefer default
      let imageSrc = src
      if (src && typeof src === 'object' && src.default) imageSrc = src.default

      // Only render <img> when we have a string/number src
      if (typeof imageSrc === 'string' || typeof imageSrc === 'number') {
        return (
          <>
            {/* Blur placeholder for progressive loading */}
            {blurPlaceholder && !isLoaded && !hasError && (
              <img
                src={blurPlaceholder}
                alt=""
                className={imgClassName || className}
                style={{
                  ...imgStyle,
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  filter: 'blur(20px)',
                  transform: 'scale(1.1)',
                  zIndex: 1
                }}
                aria-hidden="true"
              />
            )}
            <img
              src={imageSrc}
              alt={alt}
              onLoad={() => setIsLoaded(true)}
              onError={(e) => {
                setHasError(true)
                if (backUpImage) e.target.src = backUpImage
              }}
              className={imgClassName || className}
              style={{
                ...imgStyle,
                opacity: isLoaded ? 1 : 0,
                transition: 'opacity 0.3s ease-in-out',
                position: 'relative',
                zIndex: 2
              }}
              {...props}
            />
          </>
        )
      }

      // Fallback: show placeholder or null
      return placeholder || null
    }

    return (
      <div ref={imgRef} style={{ display: 'inline-block', position: 'relative', ...style }} className={className}>
        {isVisible ? renderImage() : placeholder || null}
      </div>
    )
  }
)

LazyImage.displayName = 'LazyImage'

export default LazyImage
